import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

// layouts
import { DashboardLayout } from 'src/layouts/dashboard';

// components
import { LoadingScreen } from 'src/components/loading-screen';

// auth
import { AuthGuard } from 'src/auth/guard';

// ----------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard'));
const AssetDetailView = lazy(() => import('src/pages/dashboard/details'));

// Dashboards
const AssetAdminDashBoard = lazy(() => import('src/pages/asset-admin-dashboard/index'));
const ItSupportDashBoard = lazy(() => import('src/pages/it-support-dashboard/index'));
const TechnicianDashBoard = lazy(() => import('src/pages/technician-dashboard/index'));
const UserDashBoards = lazy(() => import('src/pages/user-dashboard/index'));
const CmmsDashboard = lazy(() => import('src/pages/cmms-dashboard/index'));
// PROFILE
const ProfilePage = lazy(() => import('src/pages/profile/details'));

// ASSET
const AssetListPage = lazy(() => import('src/pages/asset/list'));
const ExpiryListPage = lazy(() => import('src/pages/asset/expiry-list'));
const ItemListPage = lazy(() => import('src/pages/asset/item-list'));
const ItemCreatePage = lazy(() => import('src/pages/asset/item-create'));
const ItemEditPage = lazy(() => import('src/pages/asset/item-edit'));
const AssetCreatePage = lazy(() => import('src/pages/asset/new'));
const AssetDetailsPage = lazy(() => import('src/pages/asset/details'));
const HardwareAssetDetailsPage = lazy(() => import('src/pages/asset/hardware-details'));
const ItemDetailsPage = lazy(() => import('src/pages/asset/item-details'));
const AssetEditPage = lazy(() => import('src/pages/asset/edit'));
const HardwareAssetEditPage = lazy(() => import('src/pages/asset/edit-hardware'));
const PdfDownloadPage = lazy(() => import('src/pages/asset/pdf'));
const HardwareAssetCreatePage = lazy(() => import('src/pages/asset/add-hardware'));
const SoftwareAssetCreatePage = lazy(() => import('src/pages/asset/add-software'));
const SoftwareAssetDetailsPage = lazy(() => import('src/pages/asset/software-details'));
const SoftwareAssetEditPage = lazy(() => import('src/pages/asset/edit-software'));
const OthersAssetCreatePage = lazy(() => import('src/pages/asset/add-others'));
const OthersAssetDetailsPage = lazy(() => import('src/pages/asset/others-details'));
const OthersAssetEditPage = lazy(() => import('src/pages/asset/edit-others'));

// Asset Management
const AllAssetsListPage = lazy(() => import('src/pages/asset-management/all-assets-list'));
const HardwareAssetListPage = lazy(() => import('src/pages/asset-management/hardware-asset-list'));
const SoftwareAssetListPage = lazy(() => import('src/pages/asset-management/software-asset-list'));
const OtherAssetListPage = lazy(() => import('src/pages/asset-management/other-asset-list'));
const OtherItemListPage = lazy(() => import('src/pages/asset-management/other-item-list'));
const OtherAlertListPage = lazy(() => import('src/pages/asset-management/other-alert-list'));
const OtherPdfPage = lazy(() => import('src/pages/asset-management/other-pdf'));
const SoftwareAlertsListPage = lazy(
  () => import('src/pages/asset-management/software-alerts-list')
);
const SoftwareTimelinePage = lazy(() => import('src/pages/asset-management/software-timeline'));

// Dynamic Asset Management
const DynamicAssetListPage = lazy(() => import('src/pages/asset-management/dynamic-asset-list'));
const DynamicItemListPage = lazy(() => import('src/pages/asset-management/dynamic-item-list'));
const DynamicAlertListPage = lazy(() => import('src/pages/asset-management/dynamic-alert-list'));
const DynamicPdfPage = lazy(() => import('src/pages/asset-management/dynamic-pdf'));

// SETTINGS
const UsersListPage = lazy(() => import('src/pages/settings/users-list'));
const UserViewPage = lazy(() => import('src/pages/settings/user-detail-view'));
const UsersCreatePage = lazy(() => import('src/pages/user/create'));
const UsersEditPage = lazy(() => import('src/pages/user/edit'));
const UsersDetailPage = lazy(() => import('src/pages/settings/user-detail-view'));
const MakeListPage = lazy(() => import('src/pages/settings/make-list'));
const VendorListPage = lazy(() => import('src/pages/settings/vendor-list'));
const LocationListPage = lazy(() => import('src/pages/settings/location-list'));
const LocationDetailPage = lazy(() => import('src/pages/settings/location-detail-view'));
const AssetIdListPage = lazy(() => import('src/pages/settings/assetId-list'));
const TicketConfgurationTabPage = lazy(() => import('src/pages/settings/ticket-configuration-tab'));
const AssetTypeListPage = lazy(() => import('src/pages/settings/asset-type-list'));
const AssetConfigViewPage = lazy(() => import('src/pages/settings/asset-config-view'));
const CmmsAssetConfigViewPage = lazy(() => import('src/pages/settings/cmms-asset-config-view'));
const AssetChecklistPage = lazy(() => import('src/pages/settings/asset-checklist'));
const AssetChecklistCreatePage = lazy(() => import('src/pages/asset-checklist/create'));
const AssetChecklistEditPage = lazy(() => import('src/pages/asset-checklist/edit'));
const AssetChecklistViewPage = lazy(() => import('src/pages/asset-checklist/view'));
const CheckListPage = lazy(() => import('src/pages/settings/checklist-list'));
const AssetTypeDetailPage = lazy(() => import('src/pages/settings/asset-type-detail'));
const AssetTagPage = lazy(() => import('src/pages/settings/tags-list'));
const TagsListPage = lazy(() => import('src/pages/settings/tags-list'));
const CompanyListPage = lazy(() => import('src/pages/settings/company-list'));
const CurrencyCreatePage = lazy(() => import('src/pages/settings/currency-create'));
const QrCodeCustomizationPage = lazy(() => import('src/pages/settings/qr-code-customization'));
const ReportConfigurationPage = lazy(() => import('src/pages/settings/report-configuration'));
const EmailConfigurationPage = lazy(() => import('src/pages/settings/email-configuration/list'));
const TicketEmailCreatePage = lazy(
  () => import('src/pages/settings/email-configuration/ticket-email-create-view')
);
const AssetEmailCreatePage = lazy(
  () => import('src/pages/settings/email-configuration/asset-email-create-view')
);
const VendorResellerListView = lazy(() => import('src/pages/settings/vendor-reseller/list'));
const VendorResellerCreateView = lazy(() => import('src/pages/settings/vendor-reseller/create'));
const VendorResellerEditView = lazy(() => import('src/pages/settings/vendor-reseller/edit'));
const VendorResellerDetailView = lazy(() => import('src/pages/settings/vendor-reseller/detail'));

// USERS
const UserListPage = lazy(() => import('src/pages/users-list/list'));
const UsersDetailsPage = lazy(() => import('src/pages/users-list/details'));
const AssetViewPage = lazy(() => import('src/pages/users-list/assset-view'));

// USERS ASSETS
const UserAssetsListPage = lazy(() => import('src/pages/user-assets/list'));
const UserAssetsNewPage = lazy(() => import('src/pages/user-assets/new'));
const UserAssetsViewPage = lazy(() => import('src/pages/user-assets/details'));
const UserAssetsEditPage = lazy(() => import('src/pages/user-assets/edit'));

// APPROVAL MANAGER ASSETS
const ApprovalManagerListPage = lazy(() => import('src/pages/approval-management/list'));
const ApprovalManagerNewEditPage = lazy(() => import('src/pages/approval-management/new'));
const ApprovalManagerViewPage = lazy(() => import('src/pages/approval-management/details'));
const ApprovalRaiseTicketPage = lazy(() => import('src/pages/approval-management/ticket-new'));
const ApprovalEditRequestPage = lazy(() => import('src/pages/approval-management/edit'));
const ApprovalEditTicketPage = lazy(() => import('src/pages/approval-management/edit-ticket'));

// USERS TICKETS
const UserTicketsListPage = lazy(() => import('src/pages/users-tickets/list'));
const GeneralTicketNewEdit = lazy(() => import('src/pages/users-tickets/general-ticket-new'));
const UserTicketsEditPage = lazy(() => import('src/pages/users-tickets/edit'));
const UserTicketNewEditPage = lazy(() => import('src/pages/users-tickets/new'));
const UserTicketsNewEditPage = lazy(() => import('src/pages/user-assets/ticket-new'));
const UserTicketsViewPage = lazy(() => import('src/pages/users-tickets/details'));
// TICKETS
const ApprovalListPage = lazy(() => import('src/pages/tickets/approval-list'));
const TicketsListPage = lazy(() => import('src/pages/tickets/ticket-list'));
const IncidentTicketsListPage = lazy(() => import('src/pages/tickets/incidents/active-list'));
const ClosedTicketsListPage = lazy(() => import('src/pages/tickets/closed-ticket-list'));
const ClosedIncidentTicketsListPage = lazy(
  () => import('src/pages/tickets/incidents/closed-ticket-list')
);
const TicketDetailPage = lazy(() => import('src/pages/tickets/details'));
const ModificationListPage = lazy(() => import('src/pages/tickets/modification-list'));
const IncidentModificationListPage = lazy(
  () => import('src/pages/tickets/incidents/modification-list')
);
const TechnicianApprovalListPage = lazy(() => import('src/pages/tickets/technician-approval-list'));
const TechnicianIncidentListPage = lazy(
  () => import('src/pages/tickets/incidents/technician-list')
);
const ItSupportTicketListPage = lazy(() => import('src/pages/tickets/it-support'));
const ItSupportTicketDetailPage = lazy(() => import('src/pages/tickets/it-support-view'));

// ASSET RELEASE
const AssetReleaseListPage = lazy(() => import('src/pages/asset-release/list'));

// approval list- manager
const ApprovalListView = lazy(() => import('src/pages/approval-list/list'));
const ApprovalListDetailView = lazy(() => import('src/pages/approval-list/details'));

// const AllTicketsListView = lazy(() => import('src/pages/allTickets/list'));

// all tickets-manager
const AllTicketsListView = lazy(() => import('src/pages/allTickets/list'));
const AllTicketsDetailView = lazy(() => import('src/pages/allTickets/details'));

// my tickets-manager
const MyTicketsListView = lazy(() => import('src/pages/myTickets-manager/list'));
const MyTicketDetailPage = lazy(() => import('src/pages/myTickets-manager/details'));

// approval list- admin
const ApprovalListAdminView = lazy(() => import('src/pages/approval-list-admin/list'));
const ApprovalListAdminDetailView = lazy(() => import('src/pages/approval-list-admin/details'));

// ASSET SPEND MANAGEMENT
const AssetSpend = lazy(() => import('src/pages/asset-spend/overview'));

// USER SPEND MANAGEMENT
const UserSpend = lazy(() => import('src/pages/asset-spend/user-spend-list'));
const UserSpendDetailPage = lazy(() => import('src/pages/asset-spend/user-spend-detail-view'));

// KnowledgeBase
const KnowledgeBaseListPage = lazy(() => import('src/pages/knowledgeBase/list'));
const KnowledgeBaseDetailPage = lazy(() => import('src/pages/knowledgeBase/detail'));

// SUBSCRIPTION
const SubscriptionViewPage = lazy(() => import('src/pages/subscription-admin/index'));

// REMOTE ASSETS
const RemoteAssetListPage = lazy(() => import('src/pages/remote-asset/list'));

// Technician ReportsTabView
const TechnicianReportsListPage = lazy(() => import('src/pages/reports/technician-reports/list'));
const TechnicianReportsViewPage = lazy(() => import('src/pages/reports/technician-reports/view'));
const AssetDepreciationReportsListView = lazy(
  () => import('src/pages/reports/asset-depreciation-reports/list')
);

// MS Azure Sign In
const MSAzureSignInView = lazy(() => import('src/pages/auth/sso/ms-azure-sign-in'));

// Technician ReportsTabView
// const TechnicianReportsListPage = lazy(() => import('src/pages/reports/technician-reports/list'));
// const AssetDepreciationReportsListView = lazy(
//   () => import('src/pages/reports/asset-depreciation-reports/list')
// );

// ALERTS
const AlertsListPage = lazy(() => import('src/pages/alerts/list'));
const WarrantySubscriptionListPage = lazy(
  () => import('src/pages/alerts/warranty-subscription-list')
);
const ActivateAmcListPage = lazy(() => import('src/pages/alerts/activate-amc'));

// Amc Providers
const AmcProvidersListPage = lazy(() => import('src/pages/amc-providers/list'));
const AmcProviderDetailPage = lazy(() => import('src/pages/amc-providers/details'));

// Amc Contracts
const AmcContractsListPage = lazy(() => import('src/pages/amc-contracts/list'));
const AmcContractCreatePage = lazy(() => import('src/pages/amc-contracts/create'));
const AmcContractDetailPage = lazy(() => import('src/pages/amc-contracts/detail'));

// PERIODIC CHECK
const PeriodicCheckListPage = lazy(() => import('src/pages/periodic-check/index'));
const PeriodicCheckViewPage = lazy(() => import('src/pages/periodic-check/view'));
const PeriodicCheckSingleUerViewPage = lazy(() => import('src/pages/periodic-check/single'));

// USER PERIODIC CHECK
const UserPeriodicCheckListPage = lazy(() => import('src/pages/user-periodic-check/list'));

// reports
const ReportsListView = lazy(() => import('src/pages/reports/reports-list'));
const VendorReportListView = lazy(() => import('src/pages/reports/vendor-report/list'));
const AssetRequestFulfimentReportListView = lazy(
  () => import('src/pages/reports/asset-request-fulfilment-report/list')
);
const AMCContractExpiryListView = lazy(() => import('src/pages/reports/amc-contract-expiry-list'));
const AMCAssetsMaintenanceListView = lazy(
  () => import('src/pages/reports/amc-assets-maintenance-list')
);
const AssetSubscriptionListView = lazy(() => import('src/pages/reports/asset-subscription-list'));
const SoftwareLicenseListView = lazy(() => import('src/pages/reports/software-license-list'));
const AssetLifecycleListView = lazy(() => import('src/pages/reports/asset-lifecycle-list'));
const ResellerReportListView = lazy(() => import('src/pages/reports/reseller-report/list'));
const ItSupportReportListView = lazy(() => import('src/pages/reports/it-support-report/list'));

// NETWORK DISCOVERY
const NetworkDevicesListView = lazy(() => import('src/pages/device-discovery/network-discovery'));

// DISCOVERY BY IMAGE
const DiscoverByImage = lazy(() => import('src/pages/device-discovery/discover-by-img'));

// ASSET AUDIT
const AssetAuditListView = lazy(() => import('src/pages/asset-audit/list'));
const AssetAuditCreateView = lazy(() => import('src/pages/asset-audit/new'));
const AssetAuditDetailPage = lazy(() => import('src/pages/asset-audit/details'));
const AssetAuditEditPage = lazy(() => import('src/pages/asset-audit/edit'));
const AssetAuditStartVerificationPage = lazy(() => import('src/pages/asset-audit/verification'));

// Maintenance Requests
const MaintenanceRequestDetailsPage = lazy(() => import('src/pages/maintenance-requests/details'));
const MaintenanceRequestListPage = lazy(() => import('src/pages/maintenance-requests/list'));
const MaintenanceRequestCreatePage = lazy(() => import('src/pages/maintenance-requests/new'));
const MaintenanceRequestEditPage = lazy(() => import('src/pages/maintenance-requests/edit'));

// Preventive Maintenance
const PreventiveMaintenanceDetailsPage = lazy(
  () => import('src/pages/preventive-maintenance/details')
);
const PreventiveMaintenanceListPage = lazy(() => import('src/pages/preventive-maintenance/list'));
const PreventiveMaintenanceCreatePage = lazy(() => import('src/pages/preventive-maintenance/new'));
const PreventiveMaintenanceEditPage = lazy(() => import('src/pages/preventive-maintenance/edit'));
const PreventiveMaintenanceCalendarPage = lazy(
  () => import('src/pages/preventive-maintenance/calendar')
);

// Inventory
const InventoryDetailsPage = lazy(() => import('src/pages/inventory/details'));
const InventoryListPage = lazy(() => import('src/pages/inventory/list'));
const InventoryCreatePage = lazy(() => import('src/pages/inventory/new'));
const InventoryEditPage = lazy(() => import('src/pages/inventory/edit'));

// Work Order
const WorkOrderDetailsPage = lazy(() => import('src/pages/work-order/details'));
const WorkOrderListPage = lazy(() => import('src/pages/work-order/list'));
const WorkOrderCreatePage = lazy(() => import('src/pages/work-order/new'));
const WorkOrderEditPage = lazy(() => import('src/pages/work-order/edit'));
const WorkOrderCalendarPage = lazy(() => import('src/pages/work-order/calendar'));

// CMMS Assets
const CmmsAssetDetailsPage = lazy(() => import('src/pages/cmms-asset/details'));
const CmmsAssetListPage = lazy(() => import('src/pages/cmms-asset/list'));
const CmmsAssetCreatePage = lazy(() => import('src/pages/cmms-asset/new'));
const CmmsAssetEditPage = lazy(() => import('src/pages/cmms-asset/edit'));

// ----------------------------------------------------------------------

export const appRoutes = [
  {
    path: '/',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      {
        path: 'dashboard',
        children: [{ path: 'dashboard', element: <IndexPage /> }],
      },
      {
        path: 'asset-admin/dashboard',
        children: [{ element: <AssetAdminDashBoard />, index: true }],
      },
      {
        path: 'it-support/dashboard',
        children: [{ element: <ItSupportDashBoard />, index: true }],
      },
      {
        path: 'technician/dashboard',
        children: [{ element: <TechnicianDashBoard />, index: true }],
      },
      {
        path: 'user/dashboard/admin',
        children: [{ element: <UserDashBoards />, index: true }],
      },
      {
        path: 'cmms-dashboard',
        children: [{ element: <CmmsDashboard />, index: true }],
      },
      {
        path: 'view',
        children: [{ path: ':id', element: <AssetDetailView /> }],
      },
      {
        path: 'profile',
        children: [{ path: 'view', element: <ProfilePage /> }],
      },
      {
        path: 'asset',
        children: [
          { element: <AssetListPage />, index: true },
          { path: 'manage_asset', element: <AssetListPage /> },
          { path: 'expiry', element: <ExpiryListPage /> },
          { path: 'manage_item', element: <ItemListPage /> },
          { path: 'manage_item/new', element: <ItemCreatePage /> },
          { path: 'new', element: <AssetCreatePage /> },
          { path: 'hardware-new', element: <HardwareAssetCreatePage /> },
          { path: 'software-new', element: <SoftwareAssetCreatePage /> },
          { path: 'others-new', element: <OthersAssetCreatePage /> },
          { path: 'manage_item/view/:id', element: <ItemDetailsPage /> },
          { path: 'view/:id', element: <AssetDetailsPage /> },
          { path: 'hardware-view/:id', element: <HardwareAssetDetailsPage /> },
          { path: 'software-view/:id', element: <SoftwareAssetDetailsPage /> },
          { path: 'others-view/:id', element: <OthersAssetDetailsPage /> },
          { path: 'edit/:category/:id', element: <AssetEditPage /> },
          { path: 'edit-hardware/:id', element: <HardwareAssetEditPage /> },
          { path: 'edit-software/:id', element: <SoftwareAssetEditPage /> },
          { path: 'edit-others/:id', element: <OthersAssetEditPage /> },
          { path: 'manage_item/edit/:id', element: <ItemEditPage /> },
          { path: 'pdf', element: <PdfDownloadPage /> },
        ],
      },
      {
        path: 'asset-management',
        children: [
          { element: <HardwareAssetListPage />, index: true },
          // hardware
          { path: 'hardware-assets', element: <HardwareAssetListPage /> },
          { path: 'hardware-assets/new/:id', element: <HardwareAssetCreatePage /> },
          { path: 'hardware-assets/view/:id', element: <HardwareAssetDetailsPage /> },
          { path: 'hardware-assets/edit/:id', element: <HardwareAssetEditPage /> },
          { path: 'hardware-assets/items', element: <ItemListPage /> },
          { path: 'hardware-assets/items/new', element: <ItemCreatePage /> },
          { path: 'hardware-assets/items/view/:id', element: <ItemDetailsPage /> },
          { path: 'hardware-assets/items/edit/:id', element: <ItemEditPage /> },
          { path: 'hardware-assets/alerts', element: <ExpiryListPage /> },
          { path: 'hardware-assets/pdf', element: <PdfDownloadPage /> },
          // software
          { path: 'software-assets', element: <SoftwareAssetListPage /> },
          { path: 'software-assets/new/:id', element: <SoftwareAssetCreatePage /> },
          { path: 'software-assets/view/:id', element: <SoftwareAssetDetailsPage /> },
          { path: 'software-assets/edit/:id', element: <SoftwareAssetEditPage /> },
          { path: 'software-assets/alerts', element: <SoftwareAlertsListPage /> },
          { path: 'software-assets/timelines', element: <SoftwareTimelinePage /> },
          // other
          { path: 'other-assets', element: <OtherAssetListPage /> },
          { path: 'other-assets/new/:id', element: <OthersAssetCreatePage /> },
          { path: 'other-assets/view/:id', element: <OthersAssetDetailsPage /> },
          { path: 'other-assets/edit/:id', element: <OthersAssetEditPage /> },
          { path: 'other-assets/items', element: <OtherItemListPage /> },
          { path: 'other-assets/items/new', element: <ItemCreatePage /> },
          { path: 'other-assets/items/view/:id', element: <ItemDetailsPage /> },
          { path: 'other-assets/items/edit/:id', element: <ItemEditPage /> },
          { path: 'other-assets/alerts', element: <OtherAlertListPage /> },
          { path: 'other-assets/pdf', element: <OtherPdfPage /> },
          // all-assets
          { path: 'all-assets', element: <AllAssetsListPage /> },
          // dynamic
          { path: 'dynamic-assets/:category', element: <DynamicAssetListPage /> },
          { path: 'dynamic-assets/:category/new/:id', element: <AssetCreatePage /> },
          { path: 'dynamic-assets/:category/view/:id', element: <AssetDetailsPage /> },
          { path: 'dynamic-assets/:category/edit/:id', element: <AssetEditPage /> },
          { path: 'dynamic-assets/:category/items', element: <DynamicItemListPage /> },
          { path: 'dynamic-assets/:category/items/new', element: <ItemCreatePage /> },
          {
            path: 'dynamic-assets/:category/items/view/:id',
            element: <ItemDetailsPage />,
          },
          { path: 'dynamic-assets/:category/items/edit/:id', element: <ItemEditPage /> },
          { path: 'dynamic-assets/:category/alerts', element: <DynamicAlertListPage /> },
          { path: 'dynamic-assets/:category/pdf', element: <DynamicPdfPage /> },
        ],
      },
      {
        path: 'settings',
        children: [
          { element: <UsersListPage />, index: true },
          { path: 'users', element: <UsersListPage /> },
          { path: 'users/view/:id', element: <UsersDetailPage /> },
          { path: 'users/create', element: <UsersCreatePage /> },
          { path: 'users/edit/:id', element: <UsersEditPage /> },
          { path: 'make', element: <MakeListPage /> },
          { path: 'vendor', element: <VendorListPage /> },
          {
            path: 'location',
            element: <LocationListPage />,
          },
          { path: 'location/view/:id', element: <LocationDetailPage /> },
          { path: 'asset_id', element: <AssetIdListPage /> },
          { path: 'stage', element: <TicketConfgurationTabPage /> },
          { path: 'asset_type', element: <AssetTypeListPage /> },
          { path: 'checklist', element: <CheckListPage /> },
          { path: 'tags', element: <AssetTagPage /> },
          // { path: 'config', element: <AssetConfigViewPage /> },
          // { path: 'cmms-config', element: <CmmsAssetConfigViewPage /> },
          { path: 'asset_checklist', element: <AssetChecklistPage /> },
          { path: 'asset_checklist/create', element: <AssetChecklistCreatePage /> },
          { path: 'asset_checklist/edit/:id', element: <AssetChecklistEditPage /> },
          { path: 'asset_checklist/view/:id', element: <AssetChecklistViewPage /> },
          { path: 'asset_type/view/:id', element: <AssetTypeDetailPage /> },
          { path: '/settings/tags', element: <TagsListPage /> },
          { path: 'company', element: <CompanyListPage /> },
          { path: 'currency-manager', element: <CurrencyCreatePage /> },
          { path: 'qr-code-customization', element: <QrCodeCustomizationPage /> },
          { path: 'report-configuration', element: <ReportConfigurationPage /> },
          { path: 'email-configuration', element: <EmailConfigurationPage /> },
          { path: 'email-configuration/ticket-email-create', element: <TicketEmailCreatePage /> },
          { path: 'email-configuration/asset-email-create', element: <AssetEmailCreatePage /> },
          { path: 'vendor-reseller', element: <VendorResellerListView /> },
          { path: 'vendor-reseller/new', element: <VendorResellerCreateView /> },
          { path: 'vendor-reseller/edit/:id', element: <VendorResellerEditView /> },
          { path: 'vendor-reseller/view/:id', element: <VendorResellerDetailView /> },
        ],
      },
      {
        path: 'asset-config',
        children: [
          { element: <AssetConfigViewPage />, index: true },
          { path: 'it-assets', element: <AssetConfigViewPage /> },
          { path: 'cmms', element: <CmmsAssetConfigViewPage /> },
        ],
      },
      {
        path: 'users',
        children: [
          { element: <UserListPage />, index: true },
          { path: 'view/:id', element: <UsersDetailsPage /> },
          { path: 'asset/view/:id', element: <AssetViewPage /> },
        ],
      },
      {
        path: 'user-assets',
        children: [
          { element: <UserAssetsListPage />, index: true },
          { path: 'new-request', element: <UserAssetsNewPage /> },
          { path: 'view/:id', element: <UserAssetsViewPage /> },
          { path: 'edit-request/:id', element: <UserAssetsEditPage /> },
        ],
      },
      {
        path: 'user-tickets',
        children: [
          { element: <UserTicketsListPage />, index: true },
          { path: 'general-ticket/new', element: <GeneralTicketNewEdit /> },
          { path: 'raise-ticket', element: <UserTicketsNewEditPage /> },
          { path: 'raise-ticket/:id', element: <UserTicketsEditPage /> },
          { path: 'view/:id', element: <UserTicketsViewPage /> },
          { path: 'new-ticket/:id', element: <UserTicketNewEditPage /> },
        ],
      },
      {
        path: 'approval',
        children: [
          { element: <ApprovalManagerListPage />, index: true },
          { path: 'new-request', element: <ApprovalManagerNewEditPage /> },
          { path: 'view/:id', element: <ApprovalManagerViewPage /> },
          { path: 'raise-ticket/:id', element: <ApprovalRaiseTicketPage /> },
          { path: 'edit-request/:id', element: <ApprovalEditRequestPage /> },
          { path: 'edit-ticket/:id', element: <ApprovalEditTicketPage /> },
        ],
      },
      {
        path: 'ticket',
        children: [
          { path: 'approval', element: <ApprovalListPage /> },
          { path: 'list', element: <TicketsListPage /> },
          { path: 'closed_list', element: <ClosedTicketsListPage /> },
          { path: 'approval/view/:id', element: <TicketDetailPage /> },
          { path: 'modification', element: <ModificationListPage /> },
          { path: 'technician_approval', element: <TechnicianApprovalListPage /> },
          { path: 'requests-approval', element: <ApprovalListAdminView /> },
          { path: 'requests-approval/view/:id', element: <ApprovalListAdminDetailView /> },
          { path: 'my-ticket/approval-list', element: <ApprovalListView /> },
          { path: 'my-ticket/all-tickets', element: <AllTicketsListView /> },
          { path: 'my-ticket/approval-list/view/:id', element: <ApprovalListDetailView /> },
          { path: 'my-ticket/all-tickets/view/:id', element: <AllTicketsDetailView /> },
          { path: 'it-support/list', element: <ItSupportTicketListPage /> },
          { path: 'it-support/view/:id', element: <ItSupportTicketDetailPage /> },
        ],
      },
      {
        path: 'incident-ticket',
        children: [
          { path: 'list', element: <IncidentTicketsListPage /> },
          { path: 'closed_list', element: <ClosedIncidentTicketsListPage /> },
          { path: 'modification', element: <IncidentModificationListPage /> },
          { path: 'technician_list', element: <TechnicianIncidentListPage /> },
        ],
      },
      {
        path: 'asset-release/:id',
        children: [{ element: <AssetReleaseListPage />, index: true }],
      },
      {
        path: 'it-support',
        children: [
          { element: <ItSupportTicketListPage />, index: true },
          { path: 'view/:id', element: <ItSupportTicketDetailPage /> },
        ],
      },
      {
        path: 'manager-approval/ticket-list',
        children: [
          { element: <MyTicketsListView />, index: true },
          { path: 'view/:id', element: <MyTicketDetailPage /> },
        ],
      },
      {
        path: 'asset-spend-management',
        children: [
          { element: <AssetSpend />, index: true },
          { path: 'asset-spend/overview', element: <AssetSpend /> },
          { path: 'user-spend', element: <UserSpend /> },
          { path: 'user-spend/view/:id', element: <UserSpendDetailPage /> },
          { path: 'user-spend/hardware-assets/view/:id', element: <HardwareAssetDetailsPage /> },
          { path: 'user-spend/software-assets/view/:id', element: <SoftwareAssetDetailsPage /> },
          { path: 'user-spend/other-assets/view/:id', element: <OthersAssetDetailsPage /> },
          { path: 'user-spend/dynamic-assets/:category/view/:id', element: <AssetDetailsPage /> },
        ],
      },
      {
        path: 'knowledge-base',
        children: [
          { element: <KnowledgeBaseListPage />, index: true },
          { path: 'view/:id', element: <KnowledgeBaseDetailPage /> },
        ],
      },
      {
        path: 'subscription',
        children: [
          { element: <SubscriptionViewPage />, index: true },
          // { path: 'view/:id', element: <KnowledgeBaseDetailPage /> },
        ],
      },
      {
        path: 'remote-assets',
        children: [
          { element: <RemoteAssetListPage />, index: true },
          { path: 'hardware/view/:id', element: <HardwareAssetDetailsPage /> },
          { path: 'software/view/:id', element: <SoftwareAssetDetailsPage /> },
          { path: 'other/view/:id', element: <OthersAssetDetailsPage /> },
          { path: 'dynamic/:category/view/:id', element: <AssetDetailsPage /> },
        ],
      },
      {
        path: 'reports',
        children: [
          { element: <ReportsListView />, index: true },
          { path: 'technician-reports', element: <TechnicianReportsListPage /> },
          { path: 'technician-reports/view/:id', element: <TechnicianReportsViewPage /> },
          { path: 'asset-depreciation-reports', element: <AssetDepreciationReportsListView /> },
          { path: 'vendor-report', element: <VendorReportListView /> },
          { path: 'amc-contract-expiry', element: <AMCContractExpiryListView /> },
          { path: 'amc-assets-maintenance', element: <AMCAssetsMaintenanceListView /> },
          { path: 'asset-subscription', element: <AssetSubscriptionListView /> },
          { path: 'software-license', element: <SoftwareLicenseListView /> },
          { path: 'asset-lifecycle', element: <AssetLifecycleListView /> },
          { path: 'reseller-report', element: <ResellerReportListView /> },
          { path: 'it-support-report', element: <ItSupportReportListView /> },
          {
            path: 'asset-request-fulfilment-report',
            element: <AssetRequestFulfimentReportListView />,
          },
        ],
      },
      {
        path: 'alerts',
        children: [
          { element: <AlertsListPage />, index: true },
          { path: 'warranty-subscriptions', element: <WarrantySubscriptionListPage /> },
          { path: 'activate-amc', element: <ActivateAmcListPage /> },
        ],
      },
      {
        path: 'amc-providers',
        children: [
          { element: <AmcProvidersListPage />, index: true },
          { path: 'view/:id', element: <AmcProviderDetailPage /> },
        ],
      },
      {
        path: 'periodic-check',
        children: [
          { element: <PeriodicCheckListPage />, index: true },
          { path: 'view/:id', element: <PeriodicCheckViewPage /> },
          { path: 'single-user/view/:id', element: <PeriodicCheckSingleUerViewPage /> },
          { path: 'user-checklist', element: <UserPeriodicCheckListPage /> },
        ],
      },
      {
        path: 'amc-contracts',
        children: [
          { element: <AmcContractsListPage />, index: true },
          { path: 'view/:id', element: <AmcContractDetailPage /> },
          { path: 'new', element: <AmcContractCreatePage /> },
        ],
      },
      {
        path: 'ms-azure-sign-in',
        children: [{ element: <MSAzureSignInView />, index: true }],
      },
      {
        path: 'network-discovery',
        children: [{ element: <NetworkDevicesListView />, index: true }],
      },
      {
        path: 'discover-by-img',
        children: [{ element: <DiscoverByImage />, index: true }],
      },
      {
        path: 'asset-audit',
        children: [
          { element: <AssetAuditListView />, index: true },
          { path: 'new', element: <AssetAuditCreateView /> },
          { path: 'view/:id', element: <AssetAuditDetailPage /> },
          { path: 'edit/:id', element: <AssetAuditEditPage /> },
          { path: 'start-verification/:id', element: <AssetAuditStartVerificationPage /> },
        ],
      },
      {
        path: 'maintenance-requests',
        children: [
          { element: <MaintenanceRequestListPage />, index: true },
          { path: 'list', element: <MaintenanceRequestListPage /> },
          { path: 'new', element: <MaintenanceRequestCreatePage /> },
          { path: 'view/:id', element: <MaintenanceRequestDetailsPage /> },
          { path: 'edit/:id', element: <MaintenanceRequestEditPage /> },
        ],
      },
      {
        path: 'preventive-maintenance',
        children: [
          { element: <PreventiveMaintenanceListPage />, index: true },
          { path: 'new', element: <PreventiveMaintenanceCreatePage /> },
          { path: 'view/:id', element: <PreventiveMaintenanceDetailsPage /> },
          { path: 'edit/:id', element: <PreventiveMaintenanceEditPage /> },
          { path: 'calendar', element: <PreventiveMaintenanceCalendarPage /> },
        ],
      },
      {
        path: 'inventory',
        children: [
          { element: <InventoryListPage />, index: true },
          { path: 'new', element: <InventoryCreatePage /> },
          { path: 'view/:id', element: <InventoryDetailsPage /> },
          { path: 'edit/:id', element: <InventoryEditPage /> },
        ],
      },
      {
        path: 'work-order',
        children: [
          { element: <WorkOrderListPage />, index: true },
          { path: 'new', element: <WorkOrderCreatePage /> },
          { path: 'view/:id', element: <WorkOrderDetailsPage /> },
          { path: 'edit/:id', element: <WorkOrderEditPage /> },
          { path: 'calendar', element: <WorkOrderCalendarPage /> },
        ],
      },
      {
        path: 'cmms-asset',
        children: [
          { element: <CmmsAssetListPage />, index: true },
          { path: 'new', element: <CmmsAssetCreatePage /> },
          { path: 'view/:id', element: <CmmsAssetDetailsPage /> },
          { path: 'edit/:id', element: <CmmsAssetEditPage /> },
        ],
      },
    ],
  },
];
